import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import NavigationButtons from '../../NavigationButtons';
import { SelectedOfferContext } from '../../../../context/SelectedOfferContext';
import Swal from 'sweetalert2';
import {
  fetchRequestOffers,
  selectRequestFinalOffer,
  selectRequestPreOffer
} from '../../../../services/comersRequestFlow';
import { isRequestBlocked } from '../../../../utils/requestStatusGuard';
import { formatNumber, parseLocaleNumber } from '../../../../utils/numberFormatter';
import {
  clampAmountToPlan,
  resolveCanonicalPair,
  resolvePlanMonthlyPayment,
  resolveResumeCanonicalPair,
  shouldUseResumeAuthoritativeOffer
} from '../../../../utils/loanCalculator';
import './OfferStep.css';

const LOAN_CALCULATOR_CONFIG = {
  URL: 'https://cdn.cirenio.com/fixed-term-rate-calculator.js',
  SCRIPT_ID: 'fixed-term-rate-calculator-script'
};

const MIN_AMOUNT = 100000;
const MAX_AMOUNT = 3000000;
const AMOUNT_STEP = 1000;
const INSTALLMENTS = [3, 6, 9, 12, 18];
const DEFAULT_RATE_PERCENT = 95;
const NO_AVAILABLE_OFFERS_CODE = 'NO_AVAILABLE_OFFERS';
const NO_AVAILABLE_OFFERS_MESSAGE = 'No se encontraron ofertas disponibles para esta solicitud.';
const INCOMPLETE_CANONICAL_OFFER_CODE = 'INCOMPLETE_CANONICAL_OFFER';
const INCOMPLETE_CANONICAL_OFFER_MESSAGE =
  'La oferta no contiene todas las condiciones económicas confirmadas. Volvé a consultar más tarde.';

const resolveApiErrorMessage = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const firstError = Object.values(validationErrors)
      .flat()
      .find(Boolean);

    if (firstError) {
      return firstError;
    }
  }

  return error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    fallback;
};

const formatCurrency = (value, preserveFractionDigits = false) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: preserveFractionDigits ? 2 : 0,
    maximumFractionDigits: preserveFractionDigits ? 2 : 0
  }).format(value ?? 0);

const formatDate = (date) =>
  date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

const formatDateFromString = (value) => {
  if (!value) return '';
  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnlyMatch
    ? new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3])
    )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDate(date);
};

const getFallbackFirstDueDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date;
};

const fallbackInstallmentValue = (amount, installments) => {
  const annualRateByInstallments = {
    3: 0.72,
    6: 0.78,
    9: 0.84,
    12: 0.9,
    18: 0.96
  };

  const annualRate = annualRateByInstallments[installments] || 0.84;
  const monthlyRate = annualRate / 12;
  const denominator = 1 - Math.pow(1 + monthlyRate, -installments);
  if (denominator <= 0) {
    return Math.round(amount / installments);
  }

  return Math.round((amount * monthlyRate) / denominator);
};

const toCanonicalOffer = (offer, selectionMode) => {
  if (!offer) {
    return null;
  }

  return {
    id: offer.offerId || offer.id || null,
    amount: Number(
      selectionMode === 'pre'
        ? (offer.maxAmount ?? offer.amount)
        : (offer.amount ?? offer.maxAmount)
    ),
    installments: Number(offer.term ?? offer.installments),
    installmentAmount: Number(
      offer.monthlyPayment ?? offer.installmentAmount ?? offer.maxInstallmentAmount
    ),
    rate: Number(offer.interestRate ?? offer.rate ?? offer.taxes),
    firstDueDate: offer.firstDueDate || ''
  };
};

const isCanonicalOfferComplete = (offer) => Boolean(
  offer?.id
  && Number.isFinite(offer.amount) && offer.amount > 0
  && Number.isFinite(offer.installments) && offer.installments > 0
  && Number.isFinite(offer.installmentAmount) && offer.installmentAmount > 0
  && Number.isFinite(offer.rate) && offer.rate > 0
  && offer.firstDueDate
);

const resolveInitialAmount = (bounds, resumeOffer = null, backendOffer = null) => {
  const lockedAmount = Number(resumeOffer?.amount || 0);
  if (lockedAmount >= bounds.min && lockedAmount <= bounds.max) {
    return lockedAmount;
  }

  const backendAmount = Number(backendOffer?.amount ?? backendOffer?.maxAmount ?? 0);
  if (backendAmount >= bounds.min && backendAmount <= bounds.max) {
    return backendAmount;
  }

  return bounds.max;
};

const OfferMockStep = ({
  onNext,
  onBack,
  onViewRequests,
  flowVariant = 'initial',
  requestId,
  formData = {},
  resumeContext = null,
  demoMode = false,
  onFatalError,
  onResumeExistingRequest,
  onRefreshResumeContext
}) => {
  const { setSelectedOffer } = useContext(SelectedOfferContext);
  const calculatorRef = useRef(null);
  const [calculatorReady, setCalculatorReady] = useState(false);
  const [amount, setAmount] = useState(MAX_AMOUNT);
  const [amountInput, setAmountInput] = useState(() => formatNumber(MAX_AMOUNT));
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [isSearchingOffers, setIsSearchingOffers] = useState(true);
  const [availableOffers, setAvailableOffers] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loadErrorCode, setLoadErrorCode] = useState('');
  const [requestRejected, setRequestRejected] = useState(false);
  const [offerSelectionMode, setOfferSelectionMode] = useState(
    flowVariant === 'approved' ? 'final' : (formData.offerSelectionMode || 'pre')
  );
  const isDemo = demoMode;
  const handledLoadErrorRef = useRef('');
  const offersSeedKeyRef = useRef('');
  const resumeInitializedRef = useRef(false);
  const isOfferSelectionLocked = flowVariant === 'approved'
    && (resumeContext?.can_reselect_offer === false || resumeContext?.contract_state?.signed === true);

  const sliderBounds = useMemo(() => {
    if (isDemo || availableOffers.length === 0) {
      return {
        min: MIN_AMOUNT,
        max: MAX_AMOUNT
      };
    }

    const min = Math.min(...availableOffers.map((offer) => Number(offer.minAmount || MIN_AMOUNT)));
    const max = Math.max(...availableOffers.map((offer) => Number(offer.maxAmount || MAX_AMOUNT)));

    return {
      min: Math.max(1000, min),
      max: Math.max(Math.max(1000, min), max)
    };
  }, [availableOffers, isDemo]);

  useEffect(() => {
    let checkInterval = null;

    const initCalculator = () => {
      if (window.FixedTermRateCalculator && !calculatorRef.current) {
        calculatorRef.current = new window.FixedTermRateCalculator();
        setCalculatorReady(true);
      }
    };

    const loadCalculator = () => {
      initCalculator();
      if (calculatorRef.current) {
        return;
      }

      const existingScript = document.getElementById(LOAN_CALCULATOR_CONFIG.SCRIPT_ID);
      if (existingScript) {
        checkInterval = setInterval(() => {
          initCalculator();
          if (calculatorRef.current && checkInterval) {
            clearInterval(checkInterval);
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.id = LOAN_CALCULATOR_CONFIG.SCRIPT_ID;
      script.src = LOAN_CALCULATOR_CONFIG.URL;
      script.async = true;
      script.onload = initCalculator;
      document.body.appendChild(script);
    };

    loadCalculator();

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, []);

  useEffect(() => {
    if (!isDemo) {
      return undefined;
    }

    const minDelayMs = 5000;
    const maxDelayMs = 10000;
    const delayMs = Math.floor(Math.random() * (maxDelayMs - minDelayMs + 1)) + minDelayMs;
    const timeoutId = setTimeout(() => {
      setIsSearchingOffers(false);
    }, delayMs);

    return () => clearTimeout(timeoutId);
  }, [isDemo]);

  useEffect(() => {
    if (isDemo) {
      return undefined;
    }

    let ignore = false;

    const loadOffers = async () => {
      if (flowVariant === 'approved' && !requestId) {
        setLoadError('No se encontró una solicitud aprobada para continuar.');
        setIsSearchingOffers(false);
        return;
      }

      setIsSearchingOffers(true);
      setLoadError('');
      setLoadErrorCode('');

      try {
        let requestUid = requestId;
        let offersPayload = null;

        if (flowVariant === 'initial') {
          if (requestUid && availableOffers.length > 0) {
            setIsSearchingOffers(false);
            return;
          }

          if (requestUid && (formData.requestOffers || []).length > 0) {
            setAvailableOffers((formData.requestOffers || []).slice().sort((left, right) => left.installments - right.installments));
            setOfferSelectionMode(formData.offerSelectionMode || 'pre');
            setIsSearchingOffers(false);
            return;
          }

          if (!requestUid) {
            setLoadError('No se encontró la solicitud evaluada para consultar ofertas.');
            setIsSearchingOffers(false);
            return;
          } else {
            offersPayload = await fetchRequestOffers(requestUid);
          }
        } else {
          offersPayload = await fetchRequestOffers(requestUid);
        }

        if (ignore) {
          return;
        }

        const preOffers = offersPayload?.preOffers || [];
        const finalOffers = offersPayload?.finalOffers || [];
        const nextSelectionMode = flowVariant === 'approved'
          ? 'final'
          : (preOffers.length > 0 ? 'pre' : 'final');
        const nextOffers = nextSelectionMode === 'pre' ? preOffers : finalOffers;

        if (nextOffers.length === 0) {
          setLoadError(NO_AVAILABLE_OFFERS_MESSAGE);
          setLoadErrorCode(NO_AVAILABLE_OFFERS_CODE);
          setAvailableOffers([]);
          return;
        }

        const incompleteOffer = nextOffers.find(
          (offer) => !isCanonicalOfferComplete(toCanonicalOffer(offer, nextSelectionMode))
        );
        if (incompleteOffer) {
          setLoadError(INCOMPLETE_CANONICAL_OFFER_MESSAGE);
          setLoadErrorCode(INCOMPLETE_CANONICAL_OFFER_CODE);
          setAvailableOffers([]);
          return;
        }

        setOfferSelectionMode(nextSelectionMode);
        setAvailableOffers(nextOffers.sort((left, right) => left.installments - right.installments));
      } catch (error) {
        console.error('Error loading request offers:', error);
        if (!ignore) {
          if (isRequestBlocked(error?.response?.data?.requestStatus)) {
            setRequestRejected(true);
            setIsSearchingOffers(false);
            return;
          }

          const apiError = error?.response?.data?.error;
          const activeRequest = error?.response?.data?.data;
          if (apiError?.code === 'REQUEST_ACTIVE_EXISTS' && activeRequest?.uid) {
            handledLoadErrorRef.current = `REQUEST_ACTIVE_EXISTS:${activeRequest.uid}`;
            Swal.fire({
              icon: 'info',
              title: 'Solicitud en curso',
              text: apiError.message || 'Este cliente ya tiene una solicitud en curso.',
              confirmButtonText: 'Continuar solicitud',
              showDenyButton: typeof onViewRequests === 'function',
              denyButtonText: 'Ver solicitudes',
              showCancelButton: true,
              cancelButtonText: 'Volver al perfil',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then((result) => {
              if (result.isConfirmed && typeof onResumeExistingRequest === 'function') {
                onResumeExistingRequest({
                  uid: activeRequest.uid,
                  resumeFlowVariant: activeRequest.resume_flow_variant || 'initial'
                });
                return;
              }

              if (result.isDenied && typeof onViewRequests === 'function') {
                onViewRequests();
                return;
              }

              if (typeof onFatalError === 'function') {
                onFatalError();
              }
            });
            setIsSearchingOffers(false);
            return;
          }

          if (apiError?.code === 'REQUEST003') {
            setLoadError(apiError.message || 'Ya existe una solicitud activa para este cliente.');
            setLoadErrorCode(apiError.code);
          } else if (apiError?.code === 'OFFERS001' && typeof onRefreshResumeContext === 'function') {
            const refreshedContext = await onRefreshResumeContext();
            if (refreshedContext?.resume_step === 'engine_processing') {
              setLoadError('La oferta todavía se está procesando.');
              setLoadErrorCode('OFFERS001');
            } else if (refreshedContext?.resume_step === 'blocked') {
              setLoadError('');
              setLoadErrorCode('');
            } else {
              setLoadError('La oferta todavía se está procesando.');
              setLoadErrorCode('OFFERS001');
            }
          } else {
            setLoadError('No se pudieron obtener las ofertas para esta solicitud.');
            setLoadErrorCode('');
          }
          setAvailableOffers([]);
        }
      } finally {
        if (!ignore) {
          setIsSearchingOffers(false);
        }
      }
    };

    loadOffers();

    return () => {
      ignore = true;
    };
  }, [
    availableOffers.length,
    flowVariant,
    formData.hiddenCustomerData,
    formData.offerSelectionMode,
    formData.requestOffers,
    formData.requestSetup,
    isDemo,
    requestId,
    onRefreshResumeContext,
    onResumeExistingRequest,
    onFatalError,
    onViewRequests
  ]);

  useEffect(() => {
    if (isDemo || availableOffers.length === 0) {
      return;
    }

    const seedKey = availableOffers.map((offer) => offer.id).join('|');
    if (offersSeedKeyRef.current === seedKey) {
      return;
    }
    offersSeedKeyRef.current = seedKey;

    const offerAmount = (offer) => Number(offer?.amount ?? offer?.maxAmount ?? 0);
    const backendOffer = offerSelectionMode === 'final'
      ? availableOffers.reduce(
        (currentMax, offer) =>
          offerAmount(offer) > offerAmount(currentMax) ? offer : currentMax,
        null
      )
      : null;
    const nextAmount = resolveInitialAmount(sliderBounds, resumeContext?.selected_offer, backendOffer);
    setAmount(nextAmount);
    setAmountInput(formatNumber(nextAmount));
  }, [
    availableOffers,
    isDemo,
    offerSelectionMode,
    resumeContext?.selected_offer,
    sliderBounds
  ]);

  useEffect(() => {
    if (isSearchingOffers || isDemo || !loadError) {
      return;
    }

    const errorKey = `${loadErrorCode}:${loadError}`;
    if (handledLoadErrorRef.current === errorKey) {
      return;
    }

    handledLoadErrorRef.current = errorKey;

    let isActive = true;

    Swal.fire({
      icon: loadErrorCode === 'REQUEST003' || loadErrorCode === NO_AVAILABLE_OFFERS_CODE ? 'info' : 'error',
      title: loadErrorCode === 'REQUEST003'
        ? 'Solicitud en curso'
        : (loadErrorCode === NO_AVAILABLE_OFFERS_CODE ? 'Sin ofertas disponibles' : 'No fue posible continuar'),
      text: loadError,
      confirmButtonText: 'Volver al perfil',
      showDenyButton: loadErrorCode === 'REQUEST003' && typeof onViewRequests === 'function',
      denyButtonText: 'Ver solicitudes',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (!isActive) {
        return;
      }

      if (result.isDenied && typeof onViewRequests === 'function') {
        onViewRequests();
        return;
      }

      if (typeof onFatalError === 'function') {
        onFatalError();
        return;
      }

      if (typeof onBack === 'function') {
        onBack();
      }
    });

    return () => {
      isActive = false;
    };
  }, [isSearchingOffers, isDemo, loadError, loadErrorCode, onBack, onFatalError, onViewRequests]);

  useEffect(() => {
    const resumeOffer = resumeContext?.selected_offer;
    const canonicalResumeOffer = toCanonicalOffer(resumeOffer, 'final');
    if (offerSelectionMode !== 'final' || !isCanonicalOfferComplete(canonicalResumeOffer)) {
      return;
    }

    if (resumeInitializedRef.current && !isOfferSelectionLocked) {
      return;
    }

    if (isOfferSelectionLocked) {
      setAmount(canonicalResumeOffer.amount);
      setAmountInput(formatNumber(canonicalResumeOffer.amount));
    }
    setSelectedPlanId(canonicalResumeOffer.id);
    resumeInitializedRef.current = true;
  }, [offerSelectionMode, resumeContext?.selected_offer, isOfferSelectionLocked]);

  const plans = useMemo(() => {
    if (!isDemo) {
      const resumeOffer = resumeContext?.selected_offer;

      return availableOffers
        .filter((offer) => Number(offer.minAmount || MIN_AMOUNT) <= amount && amount <= Number(offer.maxAmount || MAX_AMOUNT))
        .map((offer, index) => {
          const isFinalOffer = offerSelectionMode === 'final';
          const canonicalOffer = toCanonicalOffer(offer, offerSelectionMode);
          const useResumeAuthoritative = shouldUseResumeAuthoritativeOffer({
            isOfferSelectionLocked,
            resumeOffer,
            planId: canonicalOffer.id,
            selectionMode: offerSelectionMode,
            sliderAmount: amount
          });
          const resumePair = useResumeAuthoritative
            ? resolveResumeCanonicalPair(resumeOffer)
            : null;
          const listingPair = resolveCanonicalPair(offer, offerSelectionMode);
          const canonicalAmount = resumePair?.canonicalAmount ?? listingPair.canonicalAmount;
          const canonicalInstallment = resumePair?.canonicalInstallment ?? listingPair.canonicalInstallment;
          const { monthlyPayment, isReady } = resolvePlanMonthlyPayment({
            calculator: calculatorRef.current,
            calculatorReady,
            sliderAmount: amount,
            installments: canonicalOffer.installments,
            rate: canonicalOffer.rate,
            canonicalAmount,
            canonicalInstallment,
            useCanonicalInstallment: offerSelectionMode !== 'pre'
          });
          const planMaxAmount = Number(offer.maxAmount || amount);
          const apiMaxInstallment = Number(offer.maxInstallmentAmount || 0);

          return {
            id: canonicalOffer.id,
            installments: canonicalOffer.installments,
            monthlyPayment,
            firstDueDate: canonicalOffer.firstDueDate,
            rate: canonicalOffer.rate,
            maxAmount: planMaxAmount,
            minAmount: Number(offer.minAmount || MIN_AMOUNT),
            maxInstallmentAmount: apiMaxInstallment,
            riskOfferVersion: offer.riskOfferVersion || null,
            isFinalOffer,
            isInstallmentReady: isReady,
            index
          };
        });
    }

    return INSTALLMENTS.map((installments, index) => {
      let monthlyPayment = fallbackInstallmentValue(amount, installments);
      let firstDueDate = getFallbackFirstDueDate().toISOString();

      if (calculatorRef.current && calculatorReady) {
        try {
          calculatorRef.current.setup({
            amount,
            installments,
            interest: DEFAULT_RATE_PERCENT
          });
          const result = calculatorRef.current.calculate();
          const calculatedAmount = result?.installments?.[0]?.amount;
          const calculatedFirstDueDate =
            result?.firstDueDate ||
            result?.installments?.[0]?.dueDate ||
            result?.installments?.[0]?.date ||
            result?.installments?.[0]?.firstDueDate;

          if (Number.isFinite(calculatedAmount)) {
            monthlyPayment = Math.round(calculatedAmount);
          }
          if (calculatedFirstDueDate) {
            firstDueDate = calculatedFirstDueDate;
          }
        } catch (error) {
          // Keep fallback value if calculator throws.
        }
      }

      return {
        id: `mock-plan-${installments}`,
        installments,
        monthlyPayment,
        firstDueDate,
        isInstallmentReady: true,
        index
      };
    });
  }, [
    amount,
    availableOffers,
    calculatorReady,
    isDemo,
    isOfferSelectionLocked,
    offerSelectionMode,
    resumeContext?.selected_offer
  ]);

  const currentSelectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  useEffect(() => {
    if (!selectedPlanId || plans.length === 0) {
      return;
    }

    if (!plans.some((plan) => plan.id === selectedPlanId)) {
      setSelectedPlanId(null);
    }
  }, [plans, selectedPlanId]);

  const firstDueDateLabel = useMemo(() => {
    const planForDueDate = currentSelectedPlan || plans[0];
    const formatted = formatDateFromString(planForDueDate?.firstDueDate);
    if (formatted) return formatted;
    return formatDate(getFallbackFirstDueDate());
  }, [currentSelectedPlan, plans]);

  const isContinueDisabled = !currentSelectedPlan
    || (!isDemo && !currentSelectedPlan.isInstallmentReady);

  const clampAmount = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return sliderBounds.min;
    }

    const rounded = Math.round(numeric / AMOUNT_STEP) * AMOUNT_STEP;
    return Math.min(sliderBounds.max, Math.max(sliderBounds.min, rounded));
  };

  const applyAmount = (value) => {
    const clamped = clampAmount(value);
    setAmount(clamped);
    setAmountInput(formatNumber(clamped));
  };

  const handleSliderAmountChange = (event) => {
    applyAmount(Number(event.target.value));
  };

  const handleAmountInputChange = (event) => {
    const value = event.target.value.replace(/[^0-9.,]/g, '');
    setAmountInput(value);

    if (!value) {
      return;
    }

    const numericValue = parseLocaleNumber(value);
    if (Number.isNaN(numericValue)) {
      return;
    }

    setAmount(clampAmount(numericValue));
  };

  const handleAmountInputBlur = () => {
    if (!amountInput.trim()) {
      applyAmount(sliderBounds.min);
      return;
    }

    applyAmount(amount);
  };

  const handleSelectMinAmount = () => applyAmount(sliderBounds.min);
  const handleSelectMaxAmount = () => applyAmount(sliderBounds.max);

  const handleSelectPlan = (plan) => {
    if (!isDemo) {
      const nextAmount = clampAmountToPlan(
        amount,
        plan.minAmount,
        plan.maxAmount
      );
      setAmount(nextAmount);
      setAmountInput(formatNumber(nextAmount));
    }
    setSelectedPlanId(plan.id);
  };

  const handleNext = () => {
    if (!currentSelectedPlan || isContinueDisabled) {
      return;
    }

    const selectedAmount = amount;

    let selectedOffer = {
      id: currentSelectedPlan.id,
      amount: selectedAmount,
      originalAmount: selectedAmount,
      term: currentSelectedPlan.installments,
      monthlyPayment: currentSelectedPlan.monthlyPayment,
      interestRate: currentSelectedPlan.rate || DEFAULT_RATE_PERCENT,
      financed_amount: selectedAmount,
      firstDueDate: currentSelectedPlan.firstDueDate,
      riskOfferVersion: currentSelectedPlan.riskOfferVersion || null
    };

    const persistSelection = async () => {
      if (isOfferSelectionLocked) {
        setSelectedOffer(selectedOffer);
        onNext({
          selectedPlan: selectedOffer,
          requestOffers: availableOffers,
          offerSelectionMode
        });
        return;
      }

      if (!isDemo && requestId) {
        const selectionResponse = offerSelectionMode === 'final'
          ? await selectRequestFinalOffer({
            requestUid: requestId,
            offerId: currentSelectedPlan.id,
            amount: selectedAmount,
            installmentAmount: currentSelectedPlan.monthlyPayment,
            riskOfferVersion: currentSelectedPlan.riskOfferVersion
          })
          : await selectRequestPreOffer({
            requestUid: requestId,
            offerId: currentSelectedPlan.id,
            amount: selectedAmount,
            installmentAmount: currentSelectedPlan.monthlyPayment,
            installments: currentSelectedPlan.installments,
            rate: currentSelectedPlan.rate
          });

        if (isRequestBlocked(selectionResponse?.requestStatus)) {
          setRequestRejected(true);
          return;
        }

        if (offerSelectionMode === 'final' && selectionResponse?.selectedOffer) {
          const apiOffer = selectionResponse.selectedOffer;
          const finalAmount = Number(apiOffer.amount ?? selectedAmount);
          const finalInstallment = Number(apiOffer.installmentAmount ?? currentSelectedPlan.monthlyPayment);
          const finalInstallments = Number(apiOffer.installments ?? currentSelectedPlan.installments);
          const finalRate = Number(
            apiOffer.interestRate ?? apiOffer.rate ?? apiOffer.taxes ?? currentSelectedPlan.rate ?? DEFAULT_RATE_PERCENT
          );

          selectedOffer = {
            id: apiOffer.offerId || currentSelectedPlan.id,
            amount: finalAmount,
            originalAmount: finalAmount,
            term: finalInstallments,
            monthlyPayment: finalInstallment,
            interestRate: finalRate,
            financed_amount: finalAmount,
            firstDueDate: apiOffer.firstDueDate || currentSelectedPlan.firstDueDate,
            riskOfferVersion: apiOffer.riskOfferVersion || currentSelectedPlan.riskOfferVersion || null
          };

          setAmount(finalAmount);
          setAmountInput(formatNumber(finalAmount));
        }
      }

      setSelectedOffer(selectedOffer);
      onNext({
        selectedPlan: selectedOffer,
        requestOffers: availableOffers,
        offerSelectionMode
      });
    };

    persistSelection().catch((error) => {
      console.error('Error selecting offer:', error);
      Swal.fire({
        icon: 'warning',
        title: 'No se pudo confirmar la oferta',
        text: resolveApiErrorMessage(error, 'No se pudo confirmar la oferta seleccionada.'),
        confirmButtonText: 'Entendido'
      });
    });
  };

  if (isSearchingOffers) {
    return (
      <div className="container rg-request-offer-step py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <h4 className="mt-3 mb-2">Buscando ofertas...</h4>
                <p className="text-muted mb-0">Estamos calculando las mejores opciones para tu perfil.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isSearchingOffers && !isDemo && loadError) {
    return null;
  }

  if (!isSearchingOffers && requestRejected) {
    return null;
  }

  if (!isSearchingOffers && !plans.length) {
    return (
      <div className="container rg-request-offer-step py-4">
        <div className="alert alert-info text-center mb-4">
          No hay ofertas disponibles para el monto seleccionado.
        </div>
        <NavigationButtons onBack={onBack} disableNext />
      </div>
    );
  }

  return (
    <div className="container rg-request-offer-step">
      <div className="card mb-4">
        <div className="card-body">
          <div className="text-center mb-3">
            <div className="input-group mx-auto rg-request-offer-step__amount-input">
              <span className="input-group-text">$</span>
              <input
                type="text"
                className="form-control form-control-lg text-center fw-bold"
                inputMode="numeric"
                autoComplete="off"
                aria-label="Monto a solicitar"
                value={amountInput}
                onChange={handleAmountInputChange}
                onBlur={handleAmountInputBlur}
              />
            </div>
            <small className="text-muted d-block mt-2">Primer vencimiento estimado: {firstDueDateLabel}</small>
          </div>

          <input
            type="range"
            className="form-range"
            min={sliderBounds.min}
            max={sliderBounds.max}
            step={AMOUNT_STEP}
            value={amount}
            onChange={handleSliderAmountChange}
          />

          <div className="d-flex justify-content-between mt-2">
            <button type="button" className="btn btn-link p-0 text-decoration-none" onClick={handleSelectMinAmount}>
              <small>{formatCurrency(sliderBounds.min)}</small>
            </button>
            <button type="button" className="btn btn-link p-0 text-decoration-none" onClick={handleSelectMaxAmount}>
              <small>{formatCurrency(sliderBounds.max)}</small>
            </button>
          </div>
        </div>
      </div>

      <div className="row g-3">
        {plans.map((plan) => (
          <div className="col-12 col-md-6 col-lg-4" key={plan.id}>
            <button
              type="button"
              className={`card w-100 h-100 border ${
                selectedPlanId === plan.id ? 'border-primary' : 'border-light'
              }`}
              onClick={() => handleSelectPlan(plan)}
            >
              <div className="card-body text-center d-flex flex-column justify-content-center align-items-center py-3">
                <div className="badge bg-secondary mb-2">{plan.installments} cuotas</div>
                <h3 className="mb-0">
                  {plan.isInstallmentReady
                    ? formatCurrency(plan.monthlyPayment, true)
                    : 'Calculando...'}
                </h3>
              </div>
            </button>
          </div>
        ))}
      </div>

      <NavigationButtons onBack={onBack} onNext={handleNext} disableNext={isContinueDisabled} />
      <p className="text-center text-muted small mt-3 mb-0">
        {offerSelectionMode === 'pre'
          ? 'Preoferta sujeta a calificación crediticia y validación final.'
          : 'Oferta final confirmada por el análisis de riesgo.'}
      </p>
    </div>
  );
};

export default OfferMockStep;
